import React from 'react';
import {
  Breadcrumb,
  getPasteNextAnnotations,
  getDeleteNextAnnotations,
  getNestedFieldTitle,
  getBreadcrumbTitle,
} from './breadcrumbs';
import Combokeys from 'combokeys';
import { TextCollectionField } from '@scale/shared/types/params/textcollection';
import { TextCollectionAnnotation } from '@scale/shared/types/response/textcollection';
import { getDeleteMessage } from './delete';
import { isFormFields } from '@scale/shared/textcollection/field';

type CurrentMode = 'taskMode' | 'reviewMode';

interface TextCollectionProps {
  fields: TextCollectionField[];
  annotations: Record<string, TextCollectionAnnotation>;
  readOnly: boolean;
  reviewMode: boolean;
}

interface TextCollectionState {
  annotations: Record<string, TextCollectionAnnotation>;
  annotationsRef: React.MutableRefObject<Record<string, TextCollectionAnnotation>>;
  currentMode: CurrentMode;
  focus: Breadcrumb[];
  clipboard: Breadcrumb[];
  cut: boolean;
  toast: TextCollectionToast;
  expanded: string[];
  expandAllNodes: React.MutableRefObject<string[]>;
  breadcrumbs: Breadcrumb[];
  previousBreadcrumbs?: Breadcrumb[];
}

export function useTextCollection(
  props: TextCollectionProps,
): [TextCollectionState, TextCollectionDispatch] {
  const expandAllNodes = React.useRef<string[]>([]);
  const annotationsRef = React.useRef<Record<string, TextCollectionAnnotation>>(props.annotations);
  const initState: TextCollectionState = {
    annotations: annotationsRef.current,
    annotationsRef,
    currentMode: props.reviewMode ? 'reviewMode' : 'taskMode',
    focus: [],
    clipboard: [],
    cut: false,
    toast: { message: 'EMPTY' },
    expanded: [],
    expandAllNodes,
    breadcrumbs: isFormFields(props.fields) ? [{ id: props.fields[0].field_id, index: 0 }] : [],
  };

  // Keep reference to state for combokeys handlers.
  const refToState = React.useRef(initState);
  const reducer = (
    state: TextCollectionState,
    action: TextCollectionAction,
  ): TextCollectionState => {
    refToState.current = getTextCollectionState(state, action);
    return refToState.current;
  };
  const [state, dispatch] = React.useReducer(reducer, initState);

  // Watch for toast messages and clear after a timeout.
  React.useEffect(() => {
    if (state.toast.message === 'EMPTY') {
      return;
    }
    const delays = {
      COPY: 1500,
      'PASTE-ERROR': 3000,
    };
    const timer = setTimeout(() => {
      dispatch({
        type: 'TOAST',
        payload: {
          toast: {
            message: 'EMPTY',
          },
        },
      });
    }, delays[state.toast.message]);
    return () => {
      clearTimeout(timer);
    };
  }, [state.toast]);

  React.useEffect(() => {
    if (props.readOnly) {
      return;
    }

    // Lens into state using a stable reference.
    const getState = () => refToState.current;

    const showCopyToast = () => {
      const state = getState();
      if (!state.clipboard.length) {
        return;
      }
      const toast: CopyToast = {
        message: 'COPY',
        title: getBreadcrumbTitle(props.fields, state.annotations, state.clipboard),
      };
      dispatch({
        type: 'TOAST',
        payload: {
          toast,
        },
      });
    };

    const showPasteToast = () => {
      // If the paste is valid it will return new annotations.
      const state = getState();
      const annotations = getPasteNextAnnotations(
        state.annotations,
        state.clipboard,
        state.focus,
        state.cut,
      );
      if (annotations !== state.annotations) {
        return;
      }

      // Ignore root level pasting error.
      if (!state.focus.length) {
        return;
      }

      const toast: PasteErrorToast = {
        message: 'PASTE-ERROR',
        parent: getNestedFieldTitle(
          props.fields,
          state.clipboard.slice(0, state.clipboard.length - 1),
        ),
        child: getNestedFieldTitle(props.fields, state.clipboard),
      };
      dispatch({
        type: 'TOAST',
        payload: {
          toast,
        },
      });
    };

    const combokeys = new Combokeys(document.documentElement);

    combokeys.bind('mod+c', () => {
      const state = getState();
      if (state.currentMode === 'reviewMode') {
        dispatch({ type: 'COPY' });
        showCopyToast();
      }
    });

    combokeys.bind('mod+x', () => {
      const state = getState();
      if (state.currentMode === 'reviewMode') {
        dispatch({ type: 'CUT' });
        showCopyToast();
      }
    });

    combokeys.bind('mod+v', () => {
      const state = getState();
      if (state.currentMode === 'reviewMode') {
        dispatch({ type: 'PASTE' });
        showPasteToast();
      }
    });

    combokeys.bind(['del', 'backspace'], () => {
      const state = getState();
      if (state.currentMode === 'reviewMode' && state.focus.length > 0) {
        const message = getDeleteMessage(props.fields, state.annotations, state.focus);
        if (window.confirm(message)) {
          dispatch({ type: 'DELETE' });
        }
      }
    });

    combokeys.bind('mod+a', () => {
      dispatch({ type: 'EXPAND-ALL' });
      return false;
    });

    return () => {
      combokeys.detach();
    };
  }, [props.readOnly]);

  return [state, dispatch];
}

function getTextCollectionState(
  state: TextCollectionState,
  action: TextCollectionAction,
): TextCollectionState {
  state.annotationsRef.current = getAnnotations(state, action);
  return {
    annotations: state.annotationsRef.current,
    annotationsRef: state.annotationsRef,
    currentMode: getCurrentMode(state.currentMode, action),
    focus: getFocus(state.focus, action),
    clipboard: getClipboard(state, action),
    cut: getCut(state.cut, action),
    toast: getToast(state.toast, action),
    expanded: getExpanded(state, action),
    expandAllNodes: state.expandAllNodes,
    ...getBreadcrumbsState(state, action),
  };
}

const getBreadcrumbsState = (state: TextCollectionState, action: TextCollectionAction) => {
  if (action.type === 'SET-BREADCRUMBS') {
    return {
      previousBreadcrumbs: action.payload.previousBreadcrumbs || state.breadcrumbs,
      breadcrumbs: action.payload.breadcrumbs,
    };
  }
  return {
    breadcrumbs: state.breadcrumbs,
    previousBreadcrumbs: state.previousBreadcrumbs,
  };
};

function getAnnotations(
  state: TextCollectionState,
  action: TextCollectionAction,
): Record<string, TextCollectionAnnotation> {
  if (action.type === 'SET-ANNOTATIONS-TEMPORARY') {
    return action.payload.annotations;
  }
  if (action.type === 'PASTE') {
    return getPasteNextAnnotations(state.annotations, state.clipboard, state.focus, state.cut);
  }
  if (action.type === 'DELETE') {
    return getDeleteNextAnnotations(state.annotations, state.focus);
  }
  return state.annotations;
}

function getCurrentMode(currentMode: CurrentMode, action: TextCollectionAction): CurrentMode {
  if (action.type === 'SELECT-MODE') {
    return action.payload.mode;
  }
  if (action.type === 'REVIEW-EDIT') {
    return 'taskMode';
  }
  return currentMode;
}

function getFocus(focus: Breadcrumb[], action: TextCollectionAction): Breadcrumb[] {
  if (action.type === 'REVIEW-FOCUS') {
    return action.payload.breadcrumbs;
  }
  return focus;
}

function getClipboard(state: TextCollectionState, action: TextCollectionAction): Breadcrumb[] {
  // Only copy/cut/paste in review mode.
  if (state.currentMode !== 'reviewMode') {
    return state.clipboard;
  }
  if (action.type === 'COPY') {
    return state.focus;
  }
  if (action.type === 'CUT') {
    return state.focus;
  }
  return state.clipboard;
}

function getCut(cut: boolean, action: TextCollectionAction): boolean {
  if (action.type === 'CUT') {
    return true;
  }
  if (action.type === 'COPY') {
    return false;
  }
  if (action.type === 'PASTE') {
    return false;
  }
  return cut;
}

function getToast(toast: TextCollectionToast, action: TextCollectionAction): TextCollectionToast {
  if (action.type === 'TOAST') {
    return action.payload.toast;
  }
  return toast;
}

function getExpanded(state: TextCollectionState, action: TextCollectionAction): string[] {
  if (action.type === 'NODE-TOGGLE') {
    return action.payload.nodes;
  }
  if (action.type === 'EXPAND-ALL') {
    return state.expandAllNodes.current;
  }
  return state.expanded;
}

export type TextCollectionDispatch = (action: TextCollectionAction) => void;

type TextCollectionAction =
  | SelectModeAction
  | ReviewEditAction
  | ReviewFocusAction
  | CopyAction
  | CutAction
  | PasteAction
  | DeleteAction
  | SetAnnotationsTemporaryAction
  | ToastAction
  | NodeToggleAction
  | ExpandAllAction
  | SetBreadcrumbsAction;

interface SelectModeAction {
  type: 'SELECT-MODE';
  payload: {
    mode: CurrentMode;
  };
}

interface ReviewEditAction {
  type: 'REVIEW-EDIT';
  payload: {
    breadcrumbs: Breadcrumb[];
  };
}

interface ReviewFocusAction {
  type: 'REVIEW-FOCUS';
  payload: {
    breadcrumbs: Breadcrumb[];
  };
}

interface CopyAction {
  type: 'COPY';
}

interface CutAction {
  type: 'CUT';
}

interface PasteAction {
  type: 'PASTE';
}

interface DeleteAction {
  type: 'DELETE';
}

interface SetAnnotationsTemporaryAction {
  type: 'SET-ANNOTATIONS-TEMPORARY';
  payload: {
    annotations: Record<string, TextCollectionAnnotation>;
  };
}

interface CopyToast {
  message: 'COPY';
  title: string;
}

interface PasteErrorToast {
  message: 'PASTE-ERROR';
  parent: string;
  child: string;
}

interface EmptyToast {
  message: 'EMPTY';
}

export type TextCollectionToast = CopyToast | PasteErrorToast | EmptyToast;

interface ToastAction {
  type: 'TOAST';
  payload: {
    toast: TextCollectionToast;
  };
}

interface NodeToggleAction {
  type: 'NODE-TOGGLE';
  payload: {
    nodes: string[];
  };
}

interface ExpandAllAction {
  type: 'EXPAND-ALL';
}

interface SetBreadcrumbsAction {
  type: 'SET-BREADCRUMBS';
  payload: {
    breadcrumbs: Breadcrumb[];
    previousBreadcrumbs?: Breadcrumb[];
  };
}
